/**
 * SAAS-USD-BILLING-1 · Phase 3 — Merchant Performance-Fee rollup card.
 *
 * Renders two independent currency panels side-by-side. Never blends
 * CAD and USD into a single number. USD panel only shows when the
 * merchant is SaaS + USD-configured + USD-confirmed (server-derived
 * `usd.applicable` flag).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Coins, DollarSign, Info } from "lucide-react";

function _fmt(amount_minor, currency) {
  const symbol = currency === "USD" ? "US$" : "CA$";
  return `${symbol}${(Number(amount_minor || 0) / 100).toFixed(2)}`;
}

export default function PerformanceFeesCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/merchant/billing/performance-fees?period=last_30_days")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Could not load performance fees."); });
    return () => { cancelled = true; };
  }, []);

  if (err) {
    return (
      <div className="pf-card p-6" data-testid="perf-fees-error">
        <p className="text-sm text-slate-600">{String(err)}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="pf-card p-6" data-testid="perf-fees-loading">
        <p className="text-sm text-slate-500">Loading performance fees…</p>
      </div>
    );
  }

  const usdApplicable = !!data.usd?.applicable;

  return (
    <div className="pf-card p-6 mt-6" data-testid="performance-fees-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            Performance fees
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            What Cyannect earned from your verified conversions in the last 30 days.
            <span className="inline-flex items-center gap-1 ml-2 text-slate-400">
              <Info className="w-3 h-3" />
              Fees are shown per currency and never blended.
            </span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        {/* CAD panel — always rendered */}
        <div
          className="rounded-2xl border border-slate-200 bg-white p-4"
          data-testid="perf-fees-cad-panel"
        >
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest">
            <Coins className="w-3.5 h-3.5" />
            CAD performance fees
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-900"
               data-testid="perf-fees-cad-total">
            {_fmt(data.cad?.total_minor, "CAD")}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            from {data.cad?.conversion_count || 0} verified conversion
            {(data.cad?.conversion_count || 0) === 1 ? "" : "s"}
          </div>
        </div>

        {/* USD panel — only when applicable */}
        {usdApplicable ? (
          <div
            className="rounded-2xl border border-cyan-200 bg-cyan-50/40 p-4"
            data-testid="perf-fees-usd-panel"
          >
            <div className="flex items-center gap-2 text-cyan-800 text-xs uppercase tracking-widest">
              <DollarSign className="w-3.5 h-3.5" />
              USD performance fees
            </div>
            <div className="mt-2 text-2xl font-bold text-slate-900"
                 data-testid="perf-fees-usd-total">
              {_fmt(data.usd?.total_minor, "USD")}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              from {data.usd?.conversion_count || 0} verified conversion
              {(data.usd?.conversion_count || 0) === 1 ? "" : "s"}
            </div>
          </div>
        ) : (
          <div
            className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 flex items-center"
            data-testid="perf-fees-usd-na"
          >
            <p className="text-xs text-slate-500">
              USD performance fees are not applicable to your account.
              Configure USD checkout to enable USD billing.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
